import React from 'react'
import {Box, Stack, Flex, Text, Button, Checkbox, Label, Select} from '@sanity/ui'
import ReactSelect from 'react-select'

interface Product {
  fitmentAvailability: string[]
  sku: string
  name: string
  id: string
  front: boolean
  rear: boolean
  isDefaultFront: boolean
  isDefaultRear: boolean
  frontWarning?: {_ref: string; _type: 'reference'}
  rearWarning?: {_ref: string; _type: 'reference'}
}

interface ConfigWarning {
  label: string
  value: string
}

interface ProductCardProps {
  product: Product
  configWarnings: ConfigWarning[]
  fitmentAvailability: {label: string; value: string}[]
  onUpdate?: (product: Product) => void
  action?: {
    label: string
    callback: () => void
    tone?: 'primary' | 'positive' | 'caution' | 'critical'
  }
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  configWarnings,
  fitmentAvailability,
  onUpdate,
  action,
}) => {
  const updateItem = (property: keyof Product, value: any) => {
    if (property === 'front' && !value) {
      product.isDefaultFront = false
    }
    if (property === 'rear' && !value) {
      product.isDefaultRear = false
    }

    const updatedProduct = {...product, [property]: value}
    onUpdate?.(updatedProduct)
  }

  return (
    <Box
      padding={3}
      style={{
        border: '1px solid #e1e3e9',
        borderRadius: '4px',
      }}
    >
      <Stack gap={3}>
        {/* Header */}
        <Flex justify="space-between" align="center">
          <Stack gap={1}>
            <Text weight="medium">{product.name}</Text>
            <Text size={1} muted>
              {product.sku}
            </Text>
          </Stack>

          {action?.callback && (
            <Button onClick={action.callback} tone={action.tone || 'primary'} mode="ghost">
              {action?.label || 'Remove'}
            </Button>
          )}
        </Flex>

        {/* Front Options */}
        <Flex gap={4} wrap="wrap">
          <Stack gap={2}>
            <Text size={1} weight="medium">
              Front Options
            </Text>
            <Flex gap={3} align="center">
              <Checkbox
                checked={product.front}
                onChange={(event) => updateItem('front', event.currentTarget.checked)}
              />
              <Label size={1}>Front</Label>
            </Flex>
            <Flex gap={3} align="center">
              <Checkbox
                checked={product.isDefaultFront}
                disabled={!product.front}
                onChange={(event) => updateItem('isDefaultFront', event.currentTarget.checked)}
              />
              <Label size={1} muted={!product.front}>
                Pre-selected
              </Label>
            </Flex>
          </Stack>

          {configWarnings?.length > 0 && (
            <Box style={{minWidth: '200px'}}>
              <Label size={1} weight="medium">
                Front Warning
              </Label>
              <Flex gap={2}>
                <Select
                  value={product.frontWarning?._ref || ''}
                  onChange={(event) => {
                    const value = event.currentTarget.value
                    updateItem(
                      'frontWarning',
                      value ? {_ref: value, _type: 'reference'} : undefined,
                    )
                  }}
                  style={{flex: 1}}
                >
                  <option value="">Select warning...</option>
                  {configWarnings.map((warning) => (
                    <option key={warning.value} value={warning.value}>
                      {warning.label}
                    </option>
                  ))}
                </Select>
                {product.frontWarning?._ref && (
                  <Button
                    mode="ghost"
                    tone="default"
                    onClick={() => updateItem('frontWarning', undefined)}
                    text="Clear"
                   
                  />
                )}
              </Flex>
            </Box>
          )}
        </Flex>

        {/* Rear Options */}
        <Flex gap={4} wrap="wrap">
          <Stack gap={2}>
            <Text size={1} weight="medium">
              Rear Options
            </Text>
            <Flex gap={3} align="center">
              <Checkbox
                checked={product.rear}
                onChange={(event) => updateItem('rear', event.currentTarget.checked)}
              />
              <Label size={1}>Rear</Label>
            </Flex>
            <Flex gap={3} align="center">
              <Checkbox
                checked={product.isDefaultRear}
                disabled={!product.rear}
                onChange={(event) => updateItem('isDefaultRear', event.currentTarget.checked)}
              />
              <Label size={1} muted={!product.rear}>
                Pre-selected
              </Label>
            </Flex>
          </Stack>

          {configWarnings?.length > 0 && (
            <Box style={{minWidth: '200px'}}>
              <Label size={1} weight="medium">
                Rear Warning
              </Label>
              <Flex gap={2}>
                <Select
                  value={product.rearWarning?._ref || ''}
                  onChange={(event) => {
                    const value = event.currentTarget.value
                    updateItem('rearWarning', value ? {_ref: value, _type: 'reference'} : undefined)
                  }}
                  style={{flex: 1}}
                >
                  <option value="">Select warning...</option>
                  {configWarnings.map((warning) => (
                    <option key={warning.value} value={warning.value}>
                      {warning.label}
                    </option>
                  ))}
                </Select>
                {product.rearWarning?._ref && (
                  <Button
                    mode="ghost"
                    tone="default"
                    onClick={() => updateItem('rearWarning', undefined)}
                    text="Clear"
                   
                  />
                )}
              </Flex>
            </Box>
          )}
        </Flex>

        {/* Fitment Availability */}
        {fitmentAvailability?.length > 0 && (
          <Box>
            <Label size={1} weight="medium" style={{marginBottom: '8px', display: 'block'}}>
              Fitment Availability
            </Label>
            <ReactSelect
              options={fitmentAvailability}
              isMulti={true}
              isClearable={true}
              value={fitmentAvailability.filter((item) =>
                product.fitmentAvailability?.includes?.(item.value)
              )}
              onChange={(selected) => {
                const values = selected ? selected.map((option: any) => option.value) : []
                updateItem('fitmentAvailability', values)
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  fontSize: '13px',
                  minHeight: '35px',
                  borderColor: 'var(--card-border-color, #e1e3e9)',
                  backgroundColor: 'var(--card-bg-color, #ffffff)',
                  '&:hover': {
                    borderColor: 'var(--card-border-color, #e1e3e9)',
                  },
                }),
                menu: (base) => ({
                  ...base,
                  fontSize: '13px',
                  backgroundColor: 'var(--card-bg-color, #ffffff)',
                  zIndex: 1000,
                }),
                option: (base, state) => ({
                  ...base,
                  fontSize: '13px',
                  backgroundColor: state.isSelected
                    ? 'var(--card-focus-ring-color, #2276fc)'
                    : state.isFocused
                    ? 'var(--card-muted-bg-color, #f1f3f6)'
                    : 'var(--card-bg-color, #ffffff)',
                  color: state.isSelected
                    ? '#ffffff'
                    : 'var(--card-fg-color, #1f2933)',
                  '&:active': {
                    backgroundColor: 'var(--card-focus-ring-color, #2276fc)',
                  },
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: 'var(--card-muted-bg-color, #f1f3f6)',
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: 'var(--card-fg-color, #1f2933)',
                  fontSize: '13px',
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: 'var(--card-muted-fg-color, #66758d)',
                  '&:hover': {
                    backgroundColor: 'var(--card-focus-ring-color, #2276fc)',
                    color: '#ffffff',
                  },
                }),
              }}
            />
          </Box>
        )}
      </Stack>
    </Box>
  )
}

export default ProductCard
